import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatestWith, debounceTime, switchMap, tap } from 'rxjs/operators';
import * as fromActions from './actions';
import { Store } from '@ngrx/store';
import { OrganizationVehicleService, VehicleStatus } from '@services/api/api.service';
import { promiseWrapper } from '@shared/_funtions/promiseWarapper.function';
import { loadedOrganizationsSubscriber, selectSelectedOrganizationId, selectUser } from '@redux/index';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@env';

@Injectable()
export class GeoviewCustomerEffects {
  private filters: any;
  private shouldTrigger: boolean;
  private organizationId: string;
  private isPollingActive = false;
  private pollingInterval: any;
  private vehicleKeyStatus = new Map<string, string>();
  public startPolling$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.startPollingGeoviewCustomer),
    switchMap((action: any) => {
      const organizationId = action.organizationId;
      const sameOrganization = this.isPollingActive && this.organizationId === organizationId;
      const hasLiveConnections = this.hubConnections.some(
        (connection) =>
          connection?.state === HubConnectionState.Connected
          || connection?.state === HubConnectionState.Connecting,
      );

      if (sameOrganization && hasLiveConnections) {
        return [];
      }

      if (this.isPollingActive && this.organizationId && this.organizationId !== organizationId) {
        this.teardownHubConnections(this.organizationId);
      }

      this.isPollingActive = true;
      this.organizationId = organizationId;
      console.log('startPollingGeoviewCustomer', { organizationId: this.organizationId });

      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }

      // Set up periodic sync for metadata (weather, etc.)
      this.pollingInterval = setInterval(() => {
        if (this.isPollingActive) {
          this.syncMetadata();
        }
      }, 30000); // 30 seconds

      return [fromActions.obtainGeoviewCustomerJwt()];
    }),
    switchMap(async (actions) => actions),
  ));
  private hubConnections: HubConnection[] = [];
  private connectedVins: Set<string> = new Set();

  updateVehicleName$ = createEffect(() => {
    return this.actions$.pipe(ofType(fromActions.updateVehicleName),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      switchMap(async ([action, selectedOrganizationId]) => {
        await promiseWrapper(this.organizationVehicleService.updateOrganizationVehicle(selectedOrganizationId, action.vin, action.request));
        return [fromActions.getGeoviewCustomerVehicles({ filters: {} })];
      }),
      switchMap((actions) => [...actions]));
  });

  public getData$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getGeoviewCustomerVehicles),
    combineLatestWith(
      this.store.select(selectUser),
      this.store.select(selectSelectedOrganizationId),
      loadedOrganizationsSubscriber(this.store),
    ),
    tap(([action]) => {
      this.filters = { ...this.filters, ...action.filters };
      this.store.dispatch(fromActions.setLoadingGeoviewCustomer({ value: true }));
    }),
    switchMap(async ([action, user, selectedOrganizationId]) => {
      this.shouldTrigger = false;
      try {
        if (!selectedOrganizationId) {
          return [
            fromActions.setLoadingGeoviewCustomer({ value: false }),
          ];
        }

        const currentFilters = { ...this.filters, ...action.filters };
        const favorite = currentFilters.vehicleStatus?.star;
        const hasAlarms = currentFilters.vehicleStatus?.alarm;
        const dueMaintenance = currentFilters.vehicleStatus?.maintenance;
        const isRunning = currentFilters.vehicleStatus?.active;
        const dataTraffic = currentFilters.dataTraffic;

        const response = await promiseWrapper(this.organizationVehicleService.getVehiclesStatusGeoview(
          selectedOrganizationId,
          currentFilters.q || null,
          null,
          null,
          favorite,
          isRunning,
          hasAlarms,
          dueMaintenance,
          dataTraffic,
        ));

        const vehicles = response.vehicles || [];
        const totalVehicles = response.totalVehicles || 0;
        const activeVehicles = response.activeVehicles || 0;

        // Create key-value structure with VIN as key
        const vehiclesMap = vehicles.reduce((acc, vehicle) => {
          acc[vehicle.vin] = {
            vehicle: {
              ...vehicle,
              listType: 'vehicle',
            },
            geometry: {
              type: 'vehicle' as const,
              geometry: [vehicle.latitude, vehicle.longitude],
              original: vehicle,
            },
            lastUpdate: new Date().toISOString(),
          };
          return acc;
        }, {} as { [vin: string]: any });

        return [
          fromActions.setCustomerVehicles({ totalVehicles, activeVehicles, vehicles: vehiclesMap }),
          fromActions.setLoadingGeoviewCustomer({ value: false }),
          // Start websocket polling for real-time updates
          fromActions.startPollingGeoviewCustomer({ organizationId: selectedOrganizationId }),
        ];
      } catch (error) {
        return [
          fromActions.setLoadingGeoviewCustomer({ value: false }),
        ];
      }
    }),
    switchMap((actions) => [...actions]),
  ));

  public $setFilters = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.setGeoviewCustomerFilters),
      debounceTime(100),
      switchMap((action) => {
        this.filters = action.filters;
        this.shouldTrigger = true;

        return this.shouldTrigger ?
          [fromActions.getGeoviewCustomerVehicles({ filters: action.filters })]
          : [];
      }),
    );
  });
  private vehicleUpdates: Map<string, any> = new Map();
  private organizationUrls = [
    environment.sdfWsUrl + '/hubs/organization',
  ];

  public stopPolling$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.stopPollingGeoviewCustomer),
    switchMap(() => {
      const organizationId = this.organizationId;
      this.isPollingActive = false;
      this.organizationId = undefined;

      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }

      this.teardownHubConnections(organizationId);

      return [];
    }),
  ), { dispatch: false });

  public obtainGeoviewJwt$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.obtainGeoviewCustomerJwt),
    switchMap(async () => {
      try {
        const accessToken = await promiseWrapper(this.auth.getAccessTokenSilently());

        if (!accessToken) {
          console.log('No access token available for Organization WebSocket');
          return [];
        }

        this.teardownHubConnections(this.organizationId);

        // Create connections for all three WebSocket URLs
        const connections = this.organizationUrls.map((wsUrl, index) => {
          const connection = new HubConnectionBuilder()
            .withUrl(`${wsUrl}?access_token=${accessToken}`, {
              /// TODO This fix was done since it was applying twice the access token. Let's see if this fix it
              // accessTokenFactory: async () => {
              //   try {
              //     const token = await promiseWrapper(this.auth.getAccessTokenSilently());
              //     return token || '';
              //   } catch (error) {
              //     console.error('Error getting access token:', error);
              //     return '';
              //   }
              // },
            })
            .withAutomaticReconnect()
            .build();

          // Handle incoming messages
          connection.on('ReceivedMessage', (vehicleData: any) => {
            console.log(`WebSocket ${index} received update for VIN: ${vehicleData.vin}`, vehicleData);

            // Update vehicle updates map
            const existing = this.vehicleUpdates.get(vehicleData.vin) || {};
            this.vehicleUpdates.set(vehicleData.vin, { ...existing, ...vehicleData });

            // Process telemetries if available
            const telemetries: any = {};
            if (vehicleData.telemetries) {
              vehicleData.telemetries.forEach((item) => {
                telemetries[item.category] = item.physicalValue;
              });

              if (telemetries.engineSpeed != null && telemetries.engineSpeed < 600) {
                telemetries.vehicleStatus = VehicleStatus.NotRunning;
              }

              telemetries.paramsDateTime = vehicleData.timeStamp;
            }

            const position = {
              latitude: vehicleData.latitude,
              longitude: vehicleData.longitude,
              messageReceivedDateTime: vehicleData.timeStamp,
              altitude: vehicleData.altitude,
            };
            console.log('Dispatching vehicle position update for VIN:', vehicleData.vin, position, telemetries);

            // Use the efficient position update action that only updates coordinates
            this.store.dispatch(fromActions.updateVehiclePosition({
              vin: vehicleData.vin,
              latitude: vehicleData.latitude,
              longitude: vehicleData.longitude,
              vehicleStatus: telemetries.vehicleStatus,
              timestamp: vehicleData.timeStamp,
            }));
            const keyStatus = this.extractKeyStatus(vehicleData);
            if (keyStatus != null) {
              this.handleKeyStatusChange(vehicleData.vin, keyStatus);
            }
          });

          return connection;
        });

        this.hubConnections = connections;

        // Start connection function
        const startConnection = async (connection: HubConnection, index: number) => {
          try {
            await connection.start();
            await connection.invoke('JoinOrganizationGroup', { organizationId: this.organizationId });
            console.log(`Organization WebSocket ${index} connected for Organization: ${this.organizationId}`);
          } catch (err) {
            console.error(`Organization WebSocket ${index} connection error:`, err);
            if (this.isPollingActive) {
              setTimeout(() => startConnection(connection, index), 5000);
            }
          }
        };

        // Handle reconnection for each connection
        connections.forEach((connection, index) => {
          connection.onclose(() => {
            console.log(`Organization WebSocket ${index} connection closed`);
            if (this.isPollingActive) {
              startConnection(connection, index);
            }
          });
        });

        // Start all connections in parallel
        await Promise.all(connections.map((conn, idx) => startConnection(conn, idx)));

        return [];
      } catch (error) {
        console.error('Error setting up Organization WebSocket connections:', error);
        return [];
      }
    }),
  ), { dispatch: false });

  private teardownHubConnections(organizationId?: string) {
    if (this.hubConnections.length === 0) {
      return;
    }

    this.hubConnections.forEach((connection, index) => {
      if (!connection) {
        return;
      }

      if (organizationId) {
        connection.invoke('LeaveOrganizationGroup', { organizationId }).catch((e) => {
          console.error(`Error leaving organization group for connection ${index}:`, e);
        });
      }

      connection.stop().catch((e) => {
        console.error(`Error stopping hub connection ${index}:`, e);
      });
    });

    this.hubConnections = [];
    this.connectedVins.clear();
    this.vehicleUpdates.clear();
    this.vehicleKeyStatus.clear();
  }

  private extractKeyStatus(vehicleData: any): string | null {
    const keyStatusTelemetry = vehicleData.telemetries?.find(
      (item) => item.category === 'keyStatus',
    );
    if (!keyStatusTelemetry) {
      return null;
    }

    const rawValue = keyStatusTelemetry.physicalValue ?? keyStatusTelemetry.physicalValueAsString;
    if (rawValue == null) {
      return null;
    }

    const normalized = rawValue.toString().trim().toUpperCase();
    if (normalized === 'ON' || normalized === 'OFF') {
      return normalized;
    }

    return null;
  }

  private handleKeyStatusChange(vin: string, keyStatus: string): void {
    const previousStatus = this.vehicleKeyStatus.get(vin);
    if (previousStatus !== undefined && previousStatus !== keyStatus) {
      this.store.dispatch(fromActions.vehicleEngineStatusChanged({
        vin,
        engineOn: keyStatus === 'ON',
      }));
    }

    this.vehicleKeyStatus.set(vin, keyStatus);
  }

  private async syncMetadata() {
    // Sync weather data and other metadata for vehicles
    // This is called periodically to update non-telemetry data
    console.log('Syncing metadata for geoview vehicles');
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private organizationVehicleService: OrganizationVehicleService,
    private auth: AuthService,
  ) {
    this.filters = {};
  }
}
